// Client-side ed25519 signing helpers. Only the browser SPA signs and
// generates keys (the server verifies); these live under `shared/` to keep
// them out of `src/` proper while making clear they are not used by the
// Functions backend. `crypto.subtle` exists in both runtimes, so this module
// is runtime-neutral, but nothing on the server imports it.

import { b64urlEncode, b64urlDecode } from './crypto';

export interface StoredKeypair {
  priv: string; // base64url pkcs8
  pub: string; // base64url raw (32 bytes)
}

export async function generateKeypair(): Promise<StoredKeypair> {
  const kp = (await crypto.subtle.generateKey({ name: 'Ed25519' }, true, [
    'sign',
    'verify',
  ])) as CryptoKeyPair;
  const privBuf = await crypto.subtle.exportKey('pkcs8', kp.privateKey);
  const pubBuf = await crypto.subtle.exportKey('raw', kp.publicKey);
  return {
    priv: b64urlEncode(new Uint8Array(privBuf)),
    pub: b64urlEncode(new Uint8Array(pubBuf)),
  };
}

export async function importPrivateKey(b64urlPkcs8: string): Promise<CryptoKey> {
  const raw = b64urlDecode(b64urlPkcs8);
  return crypto.subtle.importKey(
    'pkcs8',
    raw as BufferSource,
    { name: 'Ed25519' },
    false,
    ['sign'],
  );
}

export async function signEnvelope(
  privKey: CryptoKey,
  envelope: Uint8Array,
): Promise<string> {
  const sig = await crypto.subtle.sign({ name: 'Ed25519' }, privKey, envelope as BufferSource);
  return b64urlEncode(new Uint8Array(sig));
}

export function randomNonceB64(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return b64urlEncode(bytes);
}
